// Models
import { ArgumentError, ArgumentNullError } from "@ms/uno-errors/lib/local/errors";
import { ExistenceStatus } from "./EntityStatus";
// Utilities
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
import remove from "lodash/remove";
/**
 * Implmentation of entity level logic for a crud supporting entity
 */ export class Entity {
    get value() {
        if (this.deltaCount === 0) {
            return this.baseline;
        }
        return this.applyDeltas();
    }
    get baseline() {
        return this._baseline;
    }
    get status() {
        return this._status;
    }
    get deltaCount() {
        return this.deltas.length;
    }
    getDelta(deltaKey) {
        return find(this.deltas, (delta)=>delta.key === deltaKey);
    }
    getBaselineDelta(deltaKey, isForward) {
        if (!this._enableHistoryTracking || !this._baselineChain) return undefined;
        const index = this._baselineChain.entries.findIndex((e)=>e.deltaKey === deltaKey);
        if (index < 0) return undefined;
        if (isForward) {
            return this._baseline.getDiff(this._baselineChain.entries[index].value);
        }
        const preOp = index === 0 ? this._baselineChain.origin : this._baselineChain.entries[index - 1].value;
        return this._baseline.getDiff(preOp);
    }
    getBaselineChainPair(deltaKey) {
        if (!this._enableHistoryTracking || !this._baselineChain) return undefined;
        const index = this._baselineChain.entries.findIndex((e)=>e.deltaKey === deltaKey);
        if (index < 0) return undefined;
        return {
            preOp: index === 0 ? this._baselineChain.origin : this._baselineChain.entries[index - 1].value,
            postOp: this._baselineChain.entries[index].value
        };
    }
    removeBaselineChainEntry(deltaKey, prunePrefix = false) {
        if (!this._enableHistoryTracking || !this._baselineChain) return false;
        const index = this._baselineChain.entries.findIndex((entry)=>entry.deltaKey === deltaKey);
        if (index < 0) return false;
        if (prunePrefix) {
            const newOrigin = this._baselineChain.entries[index].value;
            this._baselineChain.entries.splice(0, index + 1);
            if (this._baselineChain.entries.length > 0) {
                this._baselineChain.origin = newOrigin;
                return true;
            }
        } else {
            this._baselineChain.entries.splice(index, 1);
            if (this._baselineChain.entries.length > 0) {
                return true;
            }
        }
        this._baselineChain = undefined;
        return true;
    }
    pushDelta(changes, deltaKey) {
        if (isEmpty(deltaKey)) {
            throw new ArgumentNullError("deltaKey", "DeltaKey cannot be null or empty");
        }
        if (this.getDelta(deltaKey)) {
            // Delta already exits -> No-op
            return;
        }
        this.deltas.push({
            key: deltaKey,
            changes: changes,
            committed: false
        });
    }
    revertDelta(deltaKey) {
        const delta = this.getDelta(deltaKey);
        this.revertDeltaInternal(deltaKey);
        if (this._enableHistoryTracking && delta?.suspended) {
            // Barrier removed — compact any committed deltas now.
            this.performCompaction();
        }
    }
    suspendDelta(deltaKey) {
        const delta = this.getDelta(deltaKey);
        if (delta) {
            delta.suspended = true;
        }
    }
    resumeDelta(deltaKey) {
        const delta = this.getDelta(deltaKey);
        if (delta?.suspended) {
            delta.suspended = false;
            if (delta.committed) {
                // Delta was committed by server while suspended — now eligible for compaction.
                this.performCompaction();
            }
        }
    }
    commitDelta(deltaKey, additionalChanges) {
        const delta = this.getDelta(deltaKey);
        if (!delta) {
            throw new ArgumentError("deltaKey", `Could not find a delta with key ${deltaKey}`);
        }
        // Apply the delta and remove from the list
        delta.committed = true;
        // Merge additional changes into the delta to preserve order
        if (additionalChanges) {
            merge(delta.changes, additionalChanges);
        }
        this.performCompaction();
    }
    updateBaseline(changes, isCoAuthDelta = false) {
        const newBaseline = this._baseline.applyDiffs(changes);
        // Co-author propagation: only run when the caller signals a Trouter co-author delta.
        if (this._enableHistoryTracking && isCoAuthDelta && this._baselineChain) {
            const actualDelta = this._baseline.getDiff(newBaseline);
            this._baselineChain.origin = this._baselineChain.origin.applyDiffs(actualDelta);
            for (const entry of this._baselineChain.entries){
                entry.value = entry.value.applyDiffs(actualDelta);
            }
        }
        this._baseline = newBaseline;
    }
    softDelete() {
        if (this._status !== ExistenceStatus.SoftDeleted) {
            this._status = ExistenceStatus.SoftDeleted;
        }
    }
    undelete() {
        if (this._status === ExistenceStatus.SoftDeleted || this._enableHistoryTracking && this._status === ExistenceStatus.ServerDeleted) {
            this._status = ExistenceStatus.Created;
        }
    }
    markServerDeleted() {
        if (this._status !== ExistenceStatus.ServerDeleted) {
            this._status = ExistenceStatus.ServerDeleted;
        }
    }
    performCompaction() {
        const commitkeys = [];
        // Start at the beginning, remove all consecutive 'committed' deltas
        for (const delta of this.deltas){
            if (this._enableHistoryTracking) {
                if (delta.committed) {
                    commitkeys.push(delta.key);
                    if (delta.suspended) {
                        break;
                    }
                } else {
                    break;
                }
            } else {
                if (delta.committed) {
                    commitkeys.push(delta.key);
                } else {
                    break;
                }
            }
        }
        // Capture origin anchor before the first-ever chain entry so pre(first) is available.
        if (this._enableHistoryTracking && this._baselineChain === undefined && commitkeys.length > 0) {
            this._baselineChain = {
                origin: this._baseline,
                entries: []
            };
        }
        // For each delta key to commit, fold the associated changes into the singular patch, then remove it from the list of deltas
        const changes = {};
        let mergeNeeded = false;
        let runningBaseline = this._baseline; // tracks per-delta post-state for chain entries
        for (const key of commitkeys){
            const delta = this.getDelta(key);
            if (delta) {
                // Chain tracking — push per-delta post-snapshot (gated by history-tracking flag)
                if (this._enableHistoryTracking) {
                    runningBaseline = runningBaseline.applyDiffs(delta.changes);
                    if (!delta.skipBaselineChainEntry && !this._baselineChain?.entries.some((e)=>e.deltaKey === key)) {
                        this._baselineChain?.entries.push({
                            deltaKey: key,
                            value: runningBaseline
                        });
                    }
                    // Do not fold committed + suspended delta into baseline to avoid flicker
                    if (delta.suspended) {
                        continue;
                    }
                }
                mergeNeeded = true;
                merge(changes, delta.changes);
                this.revertDeltaInternal(key);
            }
        }
        // Merge the folded patch if needed
        if (mergeNeeded) {
            this.updateBaseline(changes);
        }
    }
    /**
     * Accessor to get the value directly
     * @description Marking this deprecated to note this as a temporary fix until SharePoint supports ES2017 and discontinues support for IE. Also calling this.value is not allowed strangely.
     * @deprecated
     */ getValue() {
        if (this.deltaCount === 0) {
            return this.baseline;
        }
        return this.applyDeltas();
    }
    /**
     * Applies all the deltas to a copy of the baseline and returns that value.
     * If there are no deltas, this function just returns the baseline.
     */ applyDeltas() {
        if (this._enableHistoryTracking) {
            let value = this._baseline;
            for (const delta of this.deltas){
                if (!delta.suspended) {
                    value = value.applyDiffs(delta.changes);
                }
            }
            return value;
        }
        const changeList = this.deltas.map((delta)=>delta.changes);
        let value = this._baseline;
        changeList.forEach((change)=>{
            value = value.applyDiffs(change);
        });
        return value;
    }
    /**
     * Internal reuse logic to revert or remove a delta from the list
     * @param deltaKey Key to uniquely identify the delta
     */ revertDeltaInternal(deltaKey) {
        remove(this.deltas, (update)=>update.key === deltaKey);
    }
    constructor(baseline, config){
        this._baseline = baseline;
        this.deltas = [];
        this._status = ExistenceStatus.Created;
        this._enableHistoryTracking = config?.enableHistoryTracking ?? false;
    }
}
